import React, { useState, useRef } from 'react';
import Sidebar from '../components/shared/Sidebar';
import csvService from '../services/csvService';
import vehicleSalesService, { VehicleSalesData, BulkImportResult } from '../services/vehicleSalesService';
import { CSVFile } from '../types/data/csv';
import { VehicleSalesChat } from '../components/VehicleSalesChat/VehicleSalesChat';
import './SalesDataDemoPage.css';

type ImportStep = 'upload' | 'preview' | 'mapping' | 'importing' | 'complete';

interface ColumnMapping {
  sale_id: string;
  sale_date: string;
  location_code: string;
}

const SalesDataDemoPage: React.FC = () => {
  const [currentStep, setCurrentStep] = useState<ImportStep>('upload');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [parsedCSV, setParsedCSV] = useState<CSVFile | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [columnMapping, setColumnMapping] = useState<ColumnMapping>({
    sale_id: '',
    sale_date: '',
    location_code: ''
  });
  const [isImporting, setIsImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importResult, setImportResult] = useState<BulkImportResult | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Data table states
  const [salesData, setSalesData] = useState<VehicleSalesData[]>([]);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(50);
  const [locationFilter, setLocationFilter] = useState('');
  const [searchFilter, setSearchFilter] = useState('');
  const [selectedRecord, setSelectedRecord] = useState<VehicleSalesData | null>(null);

  // No longer need AI analysis states - using chat component instead

  const handleFileSelect = async (file: File) => {
    try {
      setSelectedFile(file);
      setError(null);
      
      // Validate file type
      if (!file.name.toLowerCase().endsWith('.csv')) {
        throw new Error('Please select a CSV file');
      }

      // Validate file size (50MB limit)
      if (file.size > 50 * 1024 * 1024) {
        throw new Error('File size must be less than 50MB');
      }
      
      // Parse CSV
      const csvFile = await csvService.parseCSV(file);
      setParsedCSV(csvFile);
      setCurrentStep('preview');
      
    } catch (err: any) {
      setError(err.message || 'Failed to parse CSV file');
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleReset = () => {
    setCurrentStep('upload');
    setSelectedFile(null);
    setParsedCSV(null);
    setError(null);
    setColumnMapping({ sale_id: '', sale_date: '', location_code: '' });
    setIsImporting(false);
    setImportProgress(0);
    setImportResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const autoDetectColumns = () => {
    if (!parsedCSV) return;

    const columns = parsedCSV.columns.map(col => col.toLowerCase());
    const newMapping: ColumnMapping = { sale_id: '', sale_date: '', location_code: '' };

    // Auto-detect SaleID
    const saleIdIndex = columns.findIndex(col => 
      col === 'saleid' || col === 'sale_id' || col === 'id'
    );
    if (saleIdIndex !== -1) {
      newMapping.sale_id = parsedCSV.columns[saleIdIndex];
    }

    // Auto-detect SaleDate
    const saleDateIndex = columns.findIndex(col => 
      col === 'saledate' || col === 'sale_date' || col === 'date'
    );
    if (saleDateIndex !== -1) {
      newMapping.sale_date = parsedCSV.columns[saleDateIndex];
    }

    // Auto-detect LocationCode/DealerCode
    const locationIndex = columns.findIndex(col => 
      col === 'dealercode' || col === 'dealer_code' || col === 'locationcode' || 
      col === 'location_code' || col === 'location'
    );
    if (locationIndex !== -1) {
      newMapping.location_code = parsedCSV.columns[locationIndex];
    }

    setColumnMapping(newMapping);
  };

  const transformDataForImport = (): VehicleSalesData[] => {
    if (!parsedCSV) return [];

    const saleIdIndex = parsedCSV.columns.indexOf(columnMapping.sale_id);
    const saleDateIndex = parsedCSV.columns.indexOf(columnMapping.sale_date);
    const locationIndex = parsedCSV.columns.indexOf(columnMapping.location_code);

    return parsedCSV.rows.map((row, rowIndex) => {
      const saleDetails: any = {};

      // Add all columns except the mapped ones to sale_details
      parsedCSV.columns.forEach((col, colIndex) => {
        if (
          col !== columnMapping.sale_id &&
          col !== columnMapping.sale_date &&
          col !== columnMapping.location_code
        ) {
          saleDetails[col] = row[colIndex];
        }
      });

      return {
        sale_id: row[saleIdIndex] || `SALE-${rowIndex + 1}`,
        sale_date: row[saleDateIndex] || new Date().toISOString().split('T')[0],
        location_code: row[locationIndex] || 'UNKNOWN',
        sale_details: [saleDetails]
      };
    });
  };

  const handleImport = async () => {
    if (!parsedCSV) return;

    try {
      setIsImporting(true);
      setImportProgress(0);
      setError(null);
      setCurrentStep('importing');

      const salesData = transformDataForImport();
      
      // Batch processing for large datasets
      const BATCH_SIZE = 1000; // Import 1000 records at a time
      const totalBatches = Math.ceil(salesData.length / BATCH_SIZE);
      
      let totalSuccess = 0;
      let totalFailed = 0;
      const allErrors: Array<{ sale_id: string; error: string }> = [];

      for (let i = 0; i < totalBatches; i++) {
        const start = i * BATCH_SIZE;
        const end = Math.min((i + 1) * BATCH_SIZE, salesData.length);
        const batch = salesData.slice(start, end);

        try {
          const result = await vehicleSalesService.bulkCreate(batch);
          totalSuccess += result.success;
          totalFailed += result.failed;
          allErrors.push(...result.errors);

          // Update progress
          const progress = Math.round(((i + 1) / totalBatches) * 100);
          setImportProgress(progress);
        } catch (batchError: any) {
          console.error(`Batch ${i + 1} failed:`, batchError);
          totalFailed += batch.length;
          allErrors.push({
            sale_id: `Batch ${i + 1}`,
            error: batchError.message || 'Batch import failed'
          });
        }
      }

      setImportProgress(100);
      setImportResult({
        success: totalSuccess,
        failed: totalFailed,
        errors: allErrors
      });
      setCurrentStep('complete');

    } catch (err: any) {
      setError(err.message || 'Failed to import data');
      setImportProgress(0);
      setCurrentStep('mapping');
    } finally {
      setIsImporting(false);
    }
  };

  const validateMapping = (): boolean => {
    return !!(columnMapping.sale_id && columnMapping.sale_date && columnMapping.location_code);
  };

  const fetchSalesData = async () => {
    try {
      setIsLoadingData(true);
      const offset = (currentPage - 1) * pageSize;
      
      const filters: any = {
        limit: pageSize,
        offset: offset
      };
      
      if (locationFilter) {
        filters.location_code = locationFilter;
      }
      
      const response = await vehicleSalesService.getAll(filters);
      setSalesData(response.data);
      setTotalRecords(response.total);
    } catch (error) {
      console.error('Error fetching sales data:', error);
    } finally {
      setIsLoadingData(false);
    }
  };

  // Fetch data on mount and when filters change
  React.useEffect(() => {
    fetchSalesData();
  }, [currentPage, locationFilter]);

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this record?')) return;
    
    try {
      await vehicleSalesService.delete(id);
      fetchSalesData(); // Refresh the list
    } catch (error) {
      console.error('Error deleting record:', error);
      alert('Failed to delete record');
    }
  };

  const totalPages = Math.ceil(totalRecords / pageSize);

  // ========== AI Analysis Functions - REMOVED ==========
  // Now using VehicleSalesChat component with streaming

  // Old functions removed:
  // - getIntelligentFilterRecommendations
  // - createCSVContentFromSalesData  
  // - uploadCSVForAnalysis
  // - generateAnalysisPrompt
  // - handleAnalyze
  // - closeReport

  // ========== End AI Analysis Functions ==========

  const renderUploadStep = () => (
    <div className="sales-import-section">
      <div className="import-header">
        <h2>Import Vehicle Sales Data</h2>
        <p>Upload a CSV file containing your vehicle sales data</p>
      </div>

      <div 
        className={`file-drop-zone ${isDragging ? 'dragging' : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <div className="drop-zone-content">
          <div className="upload-icon">📁</div>
          <h3>Drop your CSV file here</h3>
          <p>or click to browse</p>
          <span className="file-requirements">Max file size: 50MB</span>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv"
          onChange={handleFileInputChange}
          style={{ display: 'none' }}
        />
      </div>

      {error && (
        <div className="error-banner">
          <span className="error-icon">⚠️</span>
          <span>{error}</span>
        </div>
      )}

      <div className="csv-format-info">
        <h4>Expected CSV Format:</h4>
        <ul>
          <li>Headers in the first row</li>
          <li>One sale per row</li>
          <li>Recommended columns: SaleID, SaleDate, LocationCode, VehicleID, Model, Price, etc.</li>
          <li>Date format: YYYY-MM-DD</li>
        </ul>
      </div>
    </div>
  );

  const renderPreviewStep = () => {
    if (!parsedCSV) return null;

    const previewRows = parsedCSV.rows.slice(0, 10);

    return (
      <div className="sales-import-section">
        <div className="import-header">
          <h2>Preview Your Data</h2>
          <p>Review the parsed CSV data before proceeding</p>
        </div>

        <div className="file-info-card">
          <div className="file-info-item">
            <strong>File Name:</strong> {parsedCSV.name}
          </div>
          <div className="file-info-item">
            <strong>Total Rows:</strong> {parsedCSV.rows.length}
          </div>
          <div className="file-info-item">
            <strong>Columns:</strong> {parsedCSV.columns.length}
          </div>
          {parsedCSV.detectedFormat && (
            <div className="file-info-item">
              <strong>Delimiter:</strong> {
                parsedCSV.detectedFormat.delimiter === ',' ? 'Comma (,)' :
                parsedCSV.detectedFormat.delimiter === ';' ? 'Semicolon (;)' :
                parsedCSV.detectedFormat.delimiter === '\t' ? 'Tab' : parsedCSV.detectedFormat.delimiter
              }
            </div>
          )}
        </div>

        <div className="csv-preview-table-container">
          <table className="csv-preview-table">
            <thead>
              <tr>
                <th className="row-number-header">#</th>
                {parsedCSV.columns.map((col, idx) => (
                  <th key={idx}>{col}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {previewRows.map((row, rowIdx) => (
                <tr key={rowIdx}>
                  <td className="row-number">{rowIdx + 1}</td>
                  {row.map((cell, cellIdx) => (
                    <td key={cellIdx}>{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {parsedCSV.rows.length > 10 && (
          <div className="preview-note">
            Showing first 10 rows of {parsedCSV.rows.length} total rows
          </div>
        )}

        <div className="action-buttons">
          <button className="btn-secondary" onClick={handleReset}>
            Upload Different File
          </button>
          <button 
            className="btn-primary" 
            onClick={() => {
              setCurrentStep('mapping');
              autoDetectColumns();
            }}
          >
            Continue to Import
          </button>
        </div>
      </div>
    );
  };

  const renderMappingStep = () => {
    if (!parsedCSV) return null;

    const availableColumns = parsedCSV.columns;
    const isValid = validateMapping();

    return (
      <div className="sales-import-section">
        <div className="import-header">
          <h2>Map Columns</h2>
          <p>Map your CSV columns to the required database fields</p>
        </div>

        <div className="mapping-container">
          <div className="mapping-info">
            <div className="info-icon">ℹ️</div>
            <div className="info-text">
              <strong>Required Fields:</strong> Map the three required columns below. All other columns will be automatically stored in the sale details.
            </div>
          </div>

          <div className="mapping-grid">
            <div className="mapping-row">
              <div className="mapping-label">
                <strong>Sale ID</strong>
                <span className="required-badge">Required</span>
                <p className="mapping-description">Unique identifier for each sale (e.g., SaleID)</p>
              </div>
              <div className="mapping-select">
                <select
                  value={columnMapping.sale_id}
                  onChange={(e) => setColumnMapping({ ...columnMapping, sale_id: e.target.value })}
                  className={columnMapping.sale_id ? 'mapped' : ''}
                >
                  <option value="">Select column...</option>
                  {availableColumns.map((col, idx) => (
                    <option key={idx} value={col}>{col}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mapping-row">
              <div className="mapping-label">
                <strong>Sale Date</strong>
                <span className="required-badge">Required</span>
                <p className="mapping-description">Date of the sale (format: YYYY-MM-DD)</p>
              </div>
              <div className="mapping-select">
                <select
                  value={columnMapping.sale_date}
                  onChange={(e) => setColumnMapping({ ...columnMapping, sale_date: e.target.value })}
                  className={columnMapping.sale_date ? 'mapped' : ''}
                >
                  <option value="">Select column...</option>
                  {availableColumns.map((col, idx) => (
                    <option key={idx} value={col}>{col}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mapping-row">
              <div className="mapping-label">
                <strong>Location Code</strong>
                <span className="required-badge">Required</span>
                <p className="mapping-description">Dealer or location code (e.g., DealerCode)</p>
              </div>
              <div className="mapping-select">
                <select
                  value={columnMapping.location_code}
                  onChange={(e) => setColumnMapping({ ...columnMapping, location_code: e.target.value })}
                  className={columnMapping.location_code ? 'mapped' : ''}
                >
                  <option value="">Select column...</option>
                  {availableColumns.map((col, idx) => (
                    <option key={idx} value={col}>{col}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="additional-columns-info">
            <h4>Additional Columns ({availableColumns.length - 3} columns)</h4>
            <p>The following columns will be stored in the sale details:</p>
            <div className="column-tags">
              {availableColumns
                .filter(col => 
                  col !== columnMapping.sale_id && 
                  col !== columnMapping.sale_date && 
                  col !== columnMapping.location_code
                )
                .map((col, idx) => (
                  <span key={idx} className="column-tag">{col}</span>
                ))
              }
            </div>
          </div>
        </div>

        {error && (
          <div className="error-banner">
            <span className="error-icon">⚠️</span>
            <span>{error}</span>
          </div>
        )}

        <div className="action-buttons">
          <button className="btn-secondary" onClick={() => setCurrentStep('preview')}>
            Back to Preview
          </button>
          <button 
            className="btn-primary" 
            onClick={handleImport}
            disabled={!isValid}
          >
            {isValid ? 'Import Data' : 'Please map all required fields'}
          </button>
        </div>
      </div>
    );
  };

  const renderImportingStep = () => {
    return (
      <div className="sales-import-section">
        <div className="import-header">
          <h2>Importing Data</h2>
          <p>Please wait while we import your data...</p>
        </div>

        <div className="import-progress-container">
          <div className="progress-icon">⚙️</div>
          <div className="progress-bar-container">
            <div className="progress-bar" style={{ width: `${importProgress}%` }}></div>
          </div>
          <div className="progress-text">{importProgress}%</div>
          {parsedCSV && (
            <>
              <p className="progress-info">Importing {parsedCSV.rows.length.toLocaleString()} records...</p>
              <p className="progress-detail">Processing in batches of 1,000 records for optimal performance</p>
            </>
          )}
        </div>
      </div>
    );
  };

  const renderCompleteStep = () => {
    return (
      <div className="sales-import-section">
        <div className="import-header">
          <h2>Import Complete!</h2>
          <p>Your vehicle sales data has been imported successfully</p>
        </div>

        <div className="import-results">
          <div className="success-icon">✅</div>
          
          {importResult && (
            <div className="result-stats">
              <div className="result-stat success">
                <div className="stat-number">{importResult.success}</div>
                <div className="stat-label">Successful</div>
              </div>
              
              {importResult.failed > 0 && (
                <div className="result-stat failed">
                  <div className="stat-number">{importResult.failed}</div>
                  <div className="stat-label">Failed</div>
                </div>
              )}
            </div>
          )}

          {importResult && importResult.errors.length > 0 && (
            <div className="import-errors">
              <h4>Import Errors ({importResult.errors.length})</h4>
              <div className="error-list">
                {importResult.errors.slice(0, 10).map((err, idx) => (
                  <div key={idx} className="error-item">
                    <strong>{err.sale_id}:</strong> {err.error}
                  </div>
                ))}
                {importResult.errors.length > 10 && (
                  <div className="error-item">
                    ... and {importResult.errors.length - 10} more errors
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="action-buttons">
          <button className="btn-secondary" onClick={handleReset}>
            Import Another File
          </button>
          <button className="btn-primary" onClick={() => {
            fetchSalesData();
            handleReset();
          }}>
            View Imported Data
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="sales-data-demo-page">
      <Sidebar />
      
      <div className="sales-data-demo-content">
        <div className="page-header">
          <h1>Sales Data Demo</h1>
          <p className="page-description">
            Vehicle sales data management and analytics
          </p>
        </div>

        {/* Step Indicator */}
        <div className="step-indicator">
          <div className={`step ${currentStep === 'upload' ? 'active' : ''} ${['preview', 'mapping', 'importing', 'complete'].includes(currentStep) ? 'completed' : ''}`}>
            <div className="step-number">1</div>
            <div className="step-label">Upload CSV</div>
          </div>
          <div className="step-divider"></div>
          <div className={`step ${currentStep === 'preview' ? 'active' : ''} ${['mapping', 'importing', 'complete'].includes(currentStep) ? 'completed' : ''}`}>
            <div className="step-number">2</div>
            <div className="step-label">Preview Data</div>
          </div>
          <div className="step-divider"></div>
          <div className={`step ${currentStep === 'mapping' ? 'active' : ''} ${['importing', 'complete'].includes(currentStep) ? 'completed' : ''}`}>
            <div className="step-number">3</div>
            <div className="step-label">Map Columns</div>
          </div>
          <div className="step-divider"></div>
          <div className={`step ${currentStep === 'importing' ? 'active' : ''} ${currentStep === 'complete' ? 'completed' : ''}`}>
            <div className="step-number">4</div>
            <div className="step-label">Import</div>
          </div>
        </div>

        {/* Step Content */}
        {currentStep === 'upload' && renderUploadStep()}
        {currentStep === 'preview' && renderPreviewStep()}
        {currentStep === 'mapping' && renderMappingStep()}
        {currentStep === 'importing' && renderImportingStep()}
        {currentStep === 'complete' && renderCompleteStep()}

        {/* Vehicle Sales Data Table */}
        <div className="sales-data-table-section">
          <div className="table-header">
            <div className="table-title">
              <h2>Vehicle Sales Records</h2>
              <span className="record-count">{totalRecords.toLocaleString()} total records</span>
            </div>
            
            <div className="table-controls">
              <input
                type="text"
                placeholder="Filter by Location Code..."
                value={locationFilter}
                onChange={(e) => {
                  setLocationFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="filter-input"
              />
              <button className="btn-secondary" onClick={fetchSalesData}>
                Refresh
              </button>
            </div>
          </div>

          {isLoadingData ? (
            <div className="loading-container">
              <div className="spinner"></div>
              <p>Loading sales data...</p>
            </div>
          ) : salesData.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">📊</div>
              <h3>No Sales Data Yet</h3>
              <p>Import your first CSV file to see vehicle sales records here</p>
            </div>
          ) : (
            <>
              <div className="data-table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Sale ID</th>
                      <th>Sale Date</th>
                      <th>Location Code</th>
                      <th>Details</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {salesData.map((record) => (
                      <tr key={record.uniqueid}>
                        <td className="sale-id">{record.sale_id}</td>
                        <td>{new Date(record.sale_date).toLocaleDateString()}</td>
                        <td className="location-code">{record.location_code}</td>
                        <td>
                          <button 
                            className="btn-view-details"
                            onClick={() => setSelectedRecord(record)}
                          >
                            View Details
                          </button>
                        </td>
                        <td className="created-date">
                          {record.created_at ? new Date(record.created_at).toLocaleString() : '-'}
                        </td>
                        <td>
                          <button 
                            className="btn-delete"
                            onClick={() => handleDelete(record.uniqueid!)}
                          >
                            🗑️
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="pagination">
                <button 
                  className="pagination-btn"
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                >
                  First
                </button>
                <button 
                  className="pagination-btn"
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  Previous
                </button>
                <span className="pagination-info">
                  Page {currentPage} of {totalPages} ({totalRecords.toLocaleString()} records)
                </span>
                <button 
                  className="pagination-btn"
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
                <button 
                  className="pagination-btn"
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                >
                  Last
                </button>
              </div>
            </>
          )}
        </div>

        {/* AI Chat Interface */}
        <div className="ai-chat-section">
          <VehicleSalesChat />
        </div>

        {/* Details Modal */}
        {selectedRecord && (
          <div className="modal-overlay" onClick={() => setSelectedRecord(null)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Sale Details: {selectedRecord.sale_id}</h3>
                <button className="modal-close" onClick={() => setSelectedRecord(null)}>✕</button>
              </div>
              
              <div className="modal-body">
                <div className="modal-two-column">
                  <div className="modal-left-column">
                    <div className="basic-info-section">
                      <h4 className="section-title">Sale Information</h4>
                      <div className="info-card">
                        <div className="info-item">
                          <span className="info-label">Sale ID:</span>
                          <span className="info-value">{selectedRecord.sale_id}</span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">Sale Date:</span>
                          <span className="info-value">{new Date(selectedRecord.sale_date).toLocaleDateString()}</span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">Location Code:</span>
                          <span className="info-value">{selectedRecord.location_code}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="modal-right-column">
                    <div className="detail-section">
                      <h4>Additional Details</h4>
                      <div className="json-display">
                        {selectedRecord.sale_details && selectedRecord.sale_details.length > 0 && (
                          <div className="detail-grid">
                            {Object.entries(selectedRecord.sale_details[0]).map(([key, value]) => (
                              <div key={key} className="detail-item">
                                <span className="detail-key">{key}:</span>
                                <span className="detail-value">{String(value)}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default SalesDataDemoPage;

